import { useCallback, useEffect, useRef, useState } from 'react';
import { writeWorkspaceHash } from '@/lib/chat-links';
import {
  runAfterSettingsGuard,
  canvasViews,
  canonicalWorkspaceHash,
  isSettingsView,
  leavesCanvas,
  leavesView,
  parseWorkspaceRoute,
  routeFor,
  type WorkspaceRoute,
} from './navigation';
import {
  canAutoOpenCanvas,
  canvasHistoryFrom,
  canvasHistoryState,
  sameCanvasObject,
  type CanvasNavigationOptions,
} from './canvas-navigation';
import {
  emptyCanvasLog,
  hasHistory,
  moveCursor,
  neighbour,
  noteCanvas,
  recordCanvas,
  type CanvasLog,
  type CanvasLogItem,
} from './canvas-log';
import type { CanvasTarget, WorkspaceView } from './types';

/** Where an object was read to: full screen and the side canvas are the same place in it. */
const scrollKey = (canvas: CanvasTarget) => JSON.stringify({ ...canvas, full: undefined });

type Route = WorkspaceRoute;
/** Editors left with unsaved changes. */
type Unsaved = { canvas: boolean; view: boolean };

export function useCanvasNavigation({
  route,
  setRoute,
  enabled,
  chatLinkId,
  threadLink = () => null,
  canvasDirty,
  setCanvasDirty,
  viewDirty,
  setViewDirty,
  canvasReading,
  setCanvasReading,
  setMobileOpen,
  navigationIntent,
}: {
  route: Route;
  setRoute: (route: Route) => void;
  enabled: boolean;
  chatLinkId: string | null;
  /** The open conversation's id when it is a saved thread: a canvas write keeps it while `chatLinkId` is still
   *  being restored (a re-bootstrap resets it), so Close/open never drops `thread=` and starts a new chat. */
  threadLink?: () => string | null;
  canvasDirty: boolean;
  setCanvasDirty: (dirty: boolean) => void;
  viewDirty: boolean;
  setViewDirty: (dirty: boolean) => void;
  canvasReading: boolean;
  setCanvasReading: (reading: boolean) => void;
  setMobileOpen: (open: boolean) => void;
  navigationIntent: { cancel: () => void };
}) {
  const currentRoute = useRef(route);
  currentRoute.current = route;
  // Whether the docked canvas's editor, and the view's own editor, hold
  // unsaved edits, as of the latest call. Each owner has its own flag: a move
  // asks only about the editors it unmounts, and Discard clears only those, so
  // an edit that stays on screen is never marked clean. A discard clears its
  // flags before its continuation navigates, so the hashchange that navigation
  // fires is never taken for another exit and asked about again.
  const dirty = useRef(canvasDirty);
  const hashChanged = useRef<(event: Event) => void>(() => {});
  const markDirty = useCallback(
    (value: boolean) => {
      dirty.current = value;
      setCanvasDirty(value);
    },
    [setCanvasDirty],
  );
  const viewEdits = useRef(viewDirty);
  const markViewDirty = useCallback(
    (value: boolean) => {
      viewEdits.current = value;
      setViewDirty(value);
    },
    [setViewDirty],
  );
  const unsaved = (current: Route, next: Route): Unsaved => ({
    canvas: dirty.current && leavesCanvas(current, next),
    view: viewEdits.current && leavesView(current, next),
  });
  // The state of the history entry on show, as of the last render, put back
  // with the page when a guarded browser move is undone.
  const entryState = useRef<unknown>(history.state);
  entryState.current = history.state;
  const scroll = useRef(0);
  const scrollByRoute = useRef(new Map<string, number>());
  const pending = useRef<(() => void) | null>(null);
  const [leavePrompt, setLeavePrompt] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  const [scrollTop, setScrollTop] = useState(
    () => canvasHistoryFrom(history.state)?.scrollTop ?? 0,
  );
  const initializedHistory = useRef(false);

  // What this chat has put on the canvas: the header's Back/Forward and its "In this chat" list.
  const [log, setLog] = useState<CanvasLog>(emptyCanvasLog);
  const logRef = useRef(log);
  logRef.current = log;
  const viaArrow = useRef(false);
  // A different chat starts a fresh list; a new chat that is only now given its id keeps what it already opened.
  const threadKey = chatLinkId ?? '';
  const loggedThread = useRef(threadKey);
  const arrived = route.canvas ? scrollKey(route.canvas) : '';
  useEffect(() => {
    const via = viaArrow.current ? 'arrow' : 'open';
    viaArrow.current = false;
    const fresh = Boolean(loggedThread.current) && loggedThread.current !== threadKey;
    loggedThread.current = threadKey;
    setLog((was) => recordCanvas(fresh ? emptyCanvasLog() : was, route.canvas, Date.now(), via));
  }, [arrived, threadKey]);
  // The one leave question (CanvasLeaveDialog): Discard clears the edits of
  // the editors being left and then leaves; Keep editing drops `leave` and
  // changes nothing.
  const askBeforeLeaving = (leave: () => void, left: Unsaved) => {
    pending.current = () => {
      if (left.canvas) markDirty(false);
      if (left.view) markViewDirty(false);
      setLeavePrompt(false);
      leave();
    };
    setLeavePrompt(true);
  };
  const guard = (left: Unsaved, leave: () => void) => {
    if (left.canvas || left.view) askBeforeLeaving(leave, left);
    else leave();
  };
  /** A move inside the view (Knowledge's tree, tabs, New page): it unmounts
   * the view's editor and keeps a docked canvas. */
  const confirmLeave = (leave: () => void) =>
    guard({ canvas: false, view: viewEdits.current }, leave);
  /** An in-app move to `next`: Settings' own guard first, then the leave
   * question when the move would unmount unsaved edits. */
  const runAfterLeaveGuard = (next: Route, complete: () => void) => {
    const current = currentRoute.current;
    runAfterSettingsGuard(current.view, next.view, () => guard(unsaved(current, next), complete));
  };

  useEffect(() => {
    if (
      initializedHistory.current ||
      !enabled ||
      !route.canvas ||
      route.canvas.kind === 'voice-preview'
    )
      return;
    initializedHistory.current = true;
    if (!canvasHistoryFrom(history.state)) {
      history.replaceState(
        { ...history.state, ...canvasHistoryState(route.canvas, 0, null) },
        '',
        location.href || location.hash,
      );
    }
  }, [enabled, route.canvas]);

  const currentLink = () => chatLinkId ?? threadLink();
  const writeRoute = (
    next: Route,
    link: string | null = currentLink(),
    mode: 'push' | 'replace' = 'push',
  ) => {
    navigationIntent.cancel();
    const previousCanvas = currentRoute.current.canvas;
    const previousView = currentRoute.current.view;
    // The hash notification is synchronous. Navigation already passed its
    // guards, so its listener must observe the accepted route immediately.
    currentRoute.current = next;
    setRoute(next);
    // Spoken previews belong only to the active conversation render. They are
    // not reloadable objects and must not be serialized into URL/history state.
    if (next.canvas?.kind === 'voice-preview') return;
    writeWorkspaceHash(
      routeFor(next.view, next.canvas, link, next.library, next.project),
      mode,
      canvasHistoryState(
        next.canvas,
        next.canvas ? (scrollByRoute.current.get(scrollKey(next.canvas)) ?? 0) : 0,
        mode === 'push' ? previousCanvas : (canvasHistoryFrom(history.state)?.previous ?? null),
        // Where the canvas was opened from: kept while objects replace each other, so Back names that page.
        !next.canvas
          ? null
          : mode === 'push' && !previousCanvas
            ? previousView
            : (canvasHistoryFrom(history.state)?.fromView ?? null),
      ),
    );
  };

  useEffect(() => {
    if (!enabled) return;
    const canonical = canonicalWorkspaceHash(location.hash);
    if (canonical && canonical !== location.hash) writeWorkspaceHash(canonical, 'replace');
  }, [enabled]);
  // One listener for the page's life that runs this render's handler. A listener re-added when
  // the chat link changes could be removed in the middle of the very hashchange it should handle
  // (Back to a project's home, where the chat navigation leaves the saved chat first).
  hashChanged.current = (event: Event) => {
    const canonical = canonicalWorkspaceHash(location.hash);
    if (enabled && canonical && canonical !== location.hash) {
      // Defer the canonical rewrite so this listener cannot synchronously
      // observe its own hashchange while the dirty guard is still active.
      queueMicrotask(() => writeWorkspaceHash(canonical, 'replace'));
      return;
    }
    const next = parseWorkspaceRoute(location.hash);
    const current = currentRoute.current;
    const left = unsaved(current, next);
    if (left.canvas || left.view) {
      // The browser (Back/Forward, a link, a hash edit) or an unguarded
      // write already moved the entry. As Settings does, put this page back
      // on top of it while asking, synchronously, so no other listener acts
      // on the rejected location: Keep editing stays here, and Discard
      // steps back to the chosen entry, whose hashchange then completes on
      // the clean page. Pushing fires no hashchange and replaces nothing
      // below, so neither choice loops.
      const previous = (event as Partial<HashChangeEvent>).oldURL;
      const hash = previous?.includes('#')
        ? previous.slice(previous.indexOf('#'))
        : routeFor(
            current.view,
            current.canvas,
            currentLink(),
            current.library,
            current.project ?? undefined,
          );
      const prior = entryState.current;
      history.pushState(
        {
          ...(prior && typeof prior === 'object' ? prior : {}),
          ...canvasHistoryState(
            current.canvas,
            scroll.current,
            canvasHistoryFrom(prior)?.previous ?? null,
          ),
        },
        '',
        `${location.pathname}${location.search}${hash}`,
      );
      askBeforeLeaving(() => history.back(), left);
      return;
    }
    runAfterSettingsGuard(
      enabled ? currentRoute.current.view : next.view,
      next.view,
      () => {
        navigationIntent.cancel();
        setRoute(next);
        setCanvasReading(false);
        const persisted = next.canvas ? (canvasHistoryFrom(history.state)?.scrollTop ?? 0) : 0;
        const restored = next.canvas
          ? persisted || scrollByRoute.current.get(scrollKey(next.canvas)) || 0
          : 0;
        scroll.current = restored;
        setScrollTop(restored);
      },
      { navigationAlreadyApplied: true },
    );
  };
  useEffect(() => {
    const changed = (event: Event) => hashChanged.current(event);
    window.addEventListener('hashchange', changed);
    return () => window.removeEventListener('hashchange', changed);
  }, []);

  const openCanvas = (
    canvas: CanvasTarget | null,
    options: CanvasNavigationOptions = {},
  ): boolean => {
    setAutoOpened(options.auto === true);
    const view: WorkspaceView =
      canvas?.kind === 'voice-preview'
        ? 'chat'
        : canvas && isSettingsView(route.view)
          ? canvasViews[canvas.kind]
          : route.view;
    const fromActivity =
      options.fromActivity === true &&
      route.canvas?.kind === 'activity' &&
      canvas?.kind === 'app-preview';
    // Opening or closing an object keeps the Library tab it came from.
    const library = view === route.view ? route.library : undefined;
    // …and the project it was opened on.
    const project = view === route.view ? route.project : undefined;
    if (
      !canAutoOpenCanvas(route.canvas, canvas, {
        auto: options.auto,
        dirty: dirty.current,
        // The first App preview of a build replaces an Activity view left open: the App must be on show, not hunted.
        // About this project is a panel beside a project's home, not reading work: it never
        // keeps a canvas the chat opens from opening.
        reading: fromActivity
          ? false
          : canvasReading || Boolean(route.canvas && route.canvas.kind !== 'project'),
      })
    )
      return false;
    // A tab/focus change within the same object preserves the mounted editor
    // and its draft, even while dirty. A committed canvas has nothing left to
    // lose; the view's own editor still asks.
    const left = unsaved(route, { view, canvas });
    if (options.committed) left.canvas = false;
    if (left.canvas || left.view) {
      askBeforeLeaving(
        () =>
          writeRoute(
            { view, canvas, library, project },
            currentLink(),
            options.replace ? 'replace' : 'push',
          ),
        left,
      );
      return false;
    }
    // A tab, activity or focus change of the object on show is the same object
    // (CANVAS-NAVIGATION rules 1 and 4): it updates this history entry, so Back
    // always returns to a different object with that object's own tab.
    const replace =
      options.replace === true ||
      Boolean(route.canvas && canvas && sameCanvasObject(route.canvas, canvas));
    let completed = false;
    runAfterSettingsGuard(route.view, view, () => {
      completed = true;
      if (options.committed) markDirty(false);
      if (route.canvas && route.canvas.kind !== 'voice-preview')
        scrollByRoute.current.set(scrollKey(route.canvas), scroll.current);
      writeRoute({ view, canvas, library, project }, currentLink(), replace ? 'replace' : 'push');
      setMobileOpen(false);
      if (!sameCanvasObject(route.canvas, canvas)) markDirty(false);
      setCanvasReading(false);
      const restored = canvas ? (scrollByRoute.current.get(scrollKey(canvas)) ?? 0) : 0;
      scroll.current = restored;
      setScrollTop(restored);
    });
    return completed;
  };

  // Closing or stepping back from the docked object leaves only its editor.
  const canvasBack = () =>
    guard({ canvas: dirty.current, view: false }, () => {
      const historyEntry = canvasHistoryFrom(history.state);
      if (
        route.canvas &&
        route.canvas.kind !== 'voice-preview' &&
        (historyEntry?.previous || (historyEntry?.fromView && historyEntry.fromView !== route.view))
      )
        history.back();
      else openCanvas(null, { replace: true });
    });

  // Choosing an App preview from the history (arrows, the list) shows exactly that build; it never follows the newest.
  const chosen = (target: CanvasTarget): CanvasTarget =>
    target.kind === 'app-preview'
      ? { ...target, pinned: true, full: route.canvas?.full }
      : { ...target, full: route.canvas?.full };
  const walk = (direction: -1 | 1) => {
    const next = neighbour(logRef.current, direction);
    if (!next) return false;
    viaArrow.current = true;
    const opened = openCanvas(chosen(next.item.target));
    if (!opened) {
      viaArrow.current = false;
      return false;
    }
    setLog((was) => moveCursor(was, next.index));
    return true;
  };
  const canvasHistory = {
    items: log.items,
    /** Two or more things opened in this chat: the arrows and the list show. */
    shown: hasHistory(log),
    back: neighbour(log, -1)?.item ?? (null as CanvasLogItem | null),
    forward: neighbour(log, 1)?.item ?? (null as CanvasLogItem | null),
    goBack: () => walk(-1),
    goForward: () => walk(1),
    open: (item: CanvasLogItem) => openCanvas(chosen(item.target)),
    note: (
      target: CanvasTarget,
      meta: { name?: string | null; kind?: string | null; unavailable?: boolean },
    ) => setLog((was) => noteCanvas(was, target, meta)),
  };

  return {
    canvasHistory,
    writeRoute,
    openCanvas,
    canvasBack,
    setCanvasDirty: markDirty,
    setViewDirty: markViewDirty,
    confirmLeave,
    runAfterLeaveGuard,
    canvasLeavePrompt: leavePrompt,
    canvasAutoOpened: autoOpened,
    canvasHasPrevious:
      route.canvas?.kind !== 'voice-preview' && Boolean(canvasHistoryFrom(history.state)?.previous),
    canvasOrigin:
      route.canvas?.kind === 'voice-preview'
        ? null
        : (canvasHistoryFrom(history.state)?.fromView ?? null),
    canvasPrevious:
      route.canvas?.kind === 'voice-preview'
        ? null
        : (canvasHistoryFrom(history.state)?.previous ?? null),
    canvasScrollTop: scrollTop,
    setCanvasScroll: (value: number) => {
      scroll.current = Math.max(0, value);
      if (!currentRoute.current.canvas || currentRoute.current.canvas.kind === 'voice-preview')
        return;
      const prior = canvasHistoryFrom(history.state);
      history.replaceState(
        {
          ...history.state,
          ...canvasHistoryState(
            currentRoute.current.canvas,
            scroll.current,
            prior?.previous ?? null,
            prior?.fromView ?? null,
          ),
        },
        '',
        location.href || location.hash,
      );
    },
    cancelCanvasLeave: () => {
      pending.current = null;
      setLeavePrompt(false);
    },
    discardCanvasLeave: () => {
      const action = pending.current;
      pending.current = null;
      action?.();
    },
  };
}
